import { observable, computed, action, makeObservable } from 'mobx';
import { TeamDecisionStore } from 'nomst/Stores/Decision/teamDecision.store';
import { OverBudgetStore } from 'nomst/Stores/Decision/overBudget.store';
import { ManagerHeaderStore } from 'nomst/Stores/Decision/managerHeader.store';
import {
    managerListSidePanelStore,
    dataManagerFactory,
    cycleRules,
    navigateToNoMst
} from 'utils/MobxDataManager/singletons';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { HeaderView } from 'nomst/Stores/Decision/Constants/headerView';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { EventStore } from 'nomst/Stores/Decision/event.store';
import { WorkflowAction } from 'nomst/Stores/Decision/Constants/workflowAction';
import {
    AmountCode,
    SidePanelListType,
    WorkflowStatusListType,
    ReportsView
} from 'typings/enums';
import { User } from 'nomst/Stores/User/user';

export class DecisionStore {

    private readonly _teamDecisionStore: TeamDecisionStore;

    private readonly _overBudgetStore: OverBudgetStore;

    private readonly _managerHeaderStore: ManagerHeaderStore;

    private readonly _managerWorkflowStore: ManagerWorkflowStore;

    private readonly _decisionDispatcher: DecisionDispatcher;

    @observable private _headerView: HeaderView;

    private readonly _configuration: Configuration;

    private readonly _eventStore: EventStore;

    @observable private _budgetNamesMap: Map<number, string> = new  Map<number, string>();

    private _topEmployeeId: Nullable<string>;

    constructor(configuration: Configuration) {
        makeObservable(this);
        this._decisionDispatcher = new DecisionDispatcher(this);
        this._teamDecisionStore = new TeamDecisionStore(
            configuration,
            this._decisionDispatcher);
        this._overBudgetStore = new OverBudgetStore();
        this._managerHeaderStore = new ManagerHeaderStore(configuration);
        this._managerWorkflowStore = new ManagerWorkflowStore(configuration);
        this._configuration = configuration;
        this._eventStore = new EventStore();
        this.periodicallyEmitEvents();
    }

    @computed
    get teamDecisionStore(): TeamDecisionStore {
        return this._teamDecisionStore;
    }

    @computed
    get overBudgetStore(): OverBudgetStore {
        return this._overBudgetStore;
    }

    @computed
    get managerHeaderStore(): ManagerHeaderStore {
        return this._managerHeaderStore;
    }

    @computed
    get managerWorkflowStore(): ManagerWorkflowStore {
        return this._managerWorkflowStore;
    }

    @computed
    get decisionDispatcher(): DecisionDispatcher {
        return this._decisionDispatcher;
    }

    @computed
    get headerView(): HeaderView {
        return this._headerView;
    }

    @computed
    get budgetNames(): Map<number, string> {
        return this._budgetNamesMap;
    }

    get cycleId(): number {
        return this._configuration.cycleId;
    }

    get topEmployeeId(): Nullable<string> {
        return this._topEmployeeId;
    }

    @action
    async setupHeader(managerSourceIds: Nullable<string[]>): Promise<void> {
        if (!managerSourceIds) {
            return;
        }

        await this.managerHeaderStore.fetchManagers(managerSourceIds);

        if (managerSourceIds.length === 1
            && this.managerHeaderStore.managers
            && this.managerHeaderStore.managers.length > 0) {
            this._headerView = HeaderView.SingleManagerHeader;
            this.managerWorkflowStore.setManagerSourceIds([this.managerHeaderStore.managers[0].employeeSourceId]);
            return;
        } else if (managerSourceIds.length > 1) {
            this.managerWorkflowStore.setManagerSourceIds(
                this.managerHeaderStore.managers.map(m => m.employeeSourceId)
            );
            this._headerView = HeaderView.MultiManagerHeader;
            return;
        }
    }

    handleDispatchWorkflowAction(workflowAction: WorkflowAction): void {
        const countriesOverBudgets = this.teamDecisionStore.countriesOverBudgets;
        if (workflowAction === WorkflowAction.SubmitReview &&
            countriesOverBudgets.find(overBudget => !overBudget.allowManagerOverBudget) &&
            !this.overBudgetStore.warnOverBudget) {
            this.overBudgetStore.turnOnOverBudgetWarning(countriesOverBudgets);
            return;
        }

        const isOverBudget = countriesOverBudgets && countriesOverBudgets.length > 0;
        const requireCommentWhenOverBudget = countriesOverBudgets.find(budget =>
            budget.allowManagerOverBudget && budget.requireCommentWhenOverBudget) !== undefined;

        this.managerWorkflowStore.invokeWorkflowSubmission(
            workflowAction,
            isOverBudget,
            requireCommentWhenOverBudget);
    }

    handleSidePanelToggled(isSidePanelOpen: boolean): void {
        managerListSidePanelStore.setToggleSidePanel(isSidePanelOpen);
        this._teamDecisionStore.setTeamBudgetBarUsage(isSidePanelOpen);
        navigateToNoMst.navToUpdateSidePanel(isSidePanelOpen);
    }

    handleProceedWorkflowSubmissionAsync(
        currentUser: User,
        currentUserAsReviewerIndex: number,
        currentReviewerIndex: number): Promise<void> {
        if (this.managerWorkflowStore.workflowAction === WorkflowAction.Approve) {
            this.teamDecisionStore.setTeamReviewability(false);
        } else if (this.managerWorkflowStore.workflowAction === WorkflowAction.SubmitReview) {
            const isReviewable = currentUserAsReviewerIndex > currentReviewerIndex;
            this.teamDecisionStore.setTeamReviewability(isReviewable);
        }

        return this.managerWorkflowStore.proceedWorkflowSubmission(currentUser);
    }

    handleDebounceAmountChanged(
        employeeSourceId: string,
        amountCode: AmountCode,
        amountCycleId: number,
        amountValue: number): void {
        const affectWorkflow = !cycleRules.isCurrentCycleInPreviewMode;
        this._eventStore.addAmountEvent(
            employeeSourceId,
            amountCode,
            amountCycleId,
            amountValue,
            affectWorkflow);
    }

    handleEmployeePerformanceRatingChangedAsync(
        employeeSourceId: string,
        oldValue: string,
        newValue: string): Promise<void> {
        return this._eventStore.emitPerformanceRatingEvent(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            employeeSourceId,
            oldValue,
            newValue);
    }

    handleResetView(): void {
        this._teamDecisionStore.clearCountryBudgetDecisions();
    }

    handleSetTeamView(selectedView: ReportsView): void {
        this._teamDecisionStore.setTeamView(selectedView);
    }

    handleSetTeamSelection(hasSelectedTeam: boolean): void {
        this._teamDecisionStore.setTeamSelection(hasSelectedTeam);
    }

    handleEmployeeSelection(employeeSourceId: Nullable<string>): void {
        if (!employeeSourceId) {
            return;
        }
        return this._teamDecisionStore.countryBudgetDecisions.forEach(countryBudget => {
            if (countryBudget.getEmployee(employeeSourceId)) {
                countryBudget?.handleEmployeeSelection(employeeSourceId);
            }
        });
    }

    async handleSetupWorkFlowManager(
        sidePanelListType: SidePanelListType,
        managerSourceIds: string[],
        workflowStatusType: WorkflowStatusListType): Promise<void> {
        await managerListSidePanelStore.setWorkflowManagerList(sidePanelListType, workflowStatusType);
        managerListSidePanelStore.setSelectedManagerIdsMap(managerSourceIds);

        const allIds = Array.from(new Set(managerListSidePanelStore.allManagerIds.concat(managerSourceIds)));
        await Promise.all([
            this.setupHeader(managerSourceIds),
            this._teamDecisionStore.fetchCountryBudgetDecisions(managerSourceIds),
            this.managerWorkflowStore
                .fetchManagerWorkflow(allIds)
        ]);
    }

    async handleSetupOverBudgetManager(
        sidePanelListType: SidePanelListType,
        managerSourceIds: string[],
        budgetCycleId: number): Promise<void> {
        managerListSidePanelStore.setSelectedManagerIdsMap(managerSourceIds);
        await managerListSidePanelStore.setOverBudgetManagerList(sidePanelListType, budgetCycleId);
        await Promise.all([
            this.setupHeader(managerSourceIds),
            this._teamDecisionStore.fetchCountryBudgetDecisions(managerSourceIds),
            this.managerWorkflowStore.fetchManagerWorkflow(managerListSidePanelStore.allManagerIds)
        ]);
    }

    handleSetUpBudgetNames(budgetNamesMap: Map<number, string>): void {
        this._budgetNamesMap = budgetNamesMap;
    }

    async fetchTopEmployeeId(): Promise<void> {
        const topEmployeeIds = await dataManagerFactory.getEmployeeDataManager()
            .getTopEmployeeIds(this._configuration.masterAccountId);
        if (topEmployeeIds && topEmployeeIds.length) {
            this._topEmployeeId = topEmployeeIds[0];
        }
    }

    private periodicallyEmitEvents(): void {
        //// TODO (PSP-20821) - how would we want to handle close browser event?
        setInterval(async () => {
            if (this._eventStore.hasAmountEvents) {
                await this._eventStore.emitAmountEvents(
                    this._configuration.masterAccountId,
                    this._configuration.cycleId);
            }
        }, 2000);
    }
}

export interface IDecisionStore extends DecisionStore {}